import { useEffect, useRef } from 'react';
import { Link } from 'wouter';
import { motion, useScroll, useTransform, useMotionValue, useSpring, useReducedMotion } from 'framer-motion';
import { Sparkles, ArrowRight, ChevronRight, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { EASE } from './helpers';

const HeroWord = ({ text, delay = 0 }: { text: string; delay?: number }) => {
    const reduce = useReducedMotion();
    if (reduce) return <>{text}</>;
    return (
        <span className="inline-block overflow-hidden align-bottom">
            <motion.span initial={{ y: '110%' }} animate={{ y: '0%' }} transition={{ duration: 0.9, ease: EASE, delay }} className="inline-block">
                {text}
            </motion.span>
        </span>
    );
};

export const Hero = () => {
    const reduce = useReducedMotion();
    const ref = useRef<HTMLDivElement>(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
    const yShift = useTransform(scrollYProgress, [0, 1], [0, -80]);
    const fade = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const sx = useSpring(mx, { stiffness: 70, damping: 16 });
    const sy = useSpring(my, { stiffness: 70, damping: 16 });
    const blob1X = useTransform(sx, (v) => v * 40);
    const blob1Y = useTransform(sy, (v) => v * 40);
    const blob2X = useTransform(sx, (v) => v * -30);
    const blob2Y = useTransform(sy, (v) => v * -25);

    useEffect(() => {
        if (reduce) return;
        const onMove = (e: MouseEvent) => {
            const x = (e.clientX / window.innerWidth - 0.5) * 2;
            const y = (e.clientY / window.innerHeight - 0.5) * 2;
            mx.set(x);
            my.set(y);
        };
        window.addEventListener('mousemove', onMove);
        return () => window.removeEventListener('mousemove', onMove);
    }, [mx, my, reduce]);

    return (
        <section ref={ref} className="relative min-h-[100vh] flex items-center justify-center pt-24 pb-16 overflow-hidden" id="top">
            <motion.div
                className="absolute -z-10 w-[60vw] h-[60vw] rounded-full blur-[110px] opacity-50"
                style={{
                    top: '-15%',
                    left: '20%',
                    background: 'radial-gradient(circle, hsl(221 83% 53% / 0.55) 0%, transparent 70%)',
                    x: blob1X,
                    y: blob1Y,
                }}
            />
            <motion.div
                className="absolute -z-10 w-[50vw] h-[50vw] rounded-full blur-[120px] opacity-40"
                style={{
                    top: '10%',
                    right: '-10%',
                    background: 'radial-gradient(circle, hsl(265 70% 58% / 0.35) 0%, transparent 70%)',
                    x: blob2X,
                    y: blob2Y,
                }}
            />
            <motion.div
                className="absolute inset-0 -z-10 opacity-[0.025]"
                style={{
                    backgroundImage: 'linear-gradient(to right, hsl(var(--foreground)) 1px, transparent 1px), linear-gradient(to bottom, hsl(var(--foreground)) 1px, transparent 1px)',
                    backgroundSize: '44px 44px',
                }}
            />

            <motion.div className="max-w-5xl mx-auto px-6 text-center" style={{ y: yShift, opacity: fade }}>
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: EASE }}
                    className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full border border-border/60 bg-background/70 backdrop-blur-sm text-muted-foreground text-xs font-medium mb-10"
                >
                    <Sparkles className="h-3 w-3 text-primary" />
                    Cognitive reconstruction, not documentation.
                </motion.div>

                <h1 className="text-5xl sm:text-6xl lg:text-[84px] font-bold tracking-[-0.035em] mb-7 leading-[0.98]">
                    <HeroWord text="What they knew shouldn't leave" delay={0.05} />
                    <br />
                    <HeroWord text="when they do." delay={0.18} />
                    <br />
                    <span className="text-primary inline-block">
                        <HeroWord text="We make sure it doesn't." delay={0.34} />
                    </span>
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: EASE, delay: 0.6 }}
                    className="text-xl sm:text-[22px] text-muted-foreground max-w-2xl mx-auto mb-12 leading-relaxed font-light"
                >
                    When a senior person leaves, the company doesn't lose a job — it loses years of judgment. Handoff sits with them for three guided sessions and reconstructs how they actually think,
                    so the next person inherits the reasoning, not a 40-page document nobody reads.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: EASE, delay: 0.7 }}
                    className="flex flex-col sm:flex-row gap-3 justify-center items-center"
                >
                    <Link href="/dashboard">
                        <Button size="lg" className="gap-2 px-7 text-base h-12 rounded-full shadow-lg shadow-primary/20 hover:shadow-primary/30 transition-shadow">
                            Book a 20-min call <ArrowRight className="h-4 w-4" />
                        </Button>
                    </Link>
                    <a href="#how-it-works" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 px-3 h-12">
                        See how it works <ChevronRight className="h-3.5 w-3.5" />
                    </a>
                </motion.div>

                <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1 }} className="mt-5 text-xs text-muted-foreground/80">
                    For every kind of departure — resignation, restructure, promotion, internal move. Two weeks, end to end.
                </motion.p>
            </motion.div>

            <motion.a
                href="#in-one-line"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 1.4 }}
                className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-muted-foreground"
                aria-label="Scroll to read"
            >
                <span className="text-[10px] uppercase tracking-[0.2em] font-semibold">Scroll</span>
                <motion.div animate={reduce ? undefined : { y: [0, 6, 0] }} transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}>
                    <ChevronDown className="h-4 w-4" />
                </motion.div>
            </motion.a>
        </section>
    );
};
